import type { ConnectorLookup } from '../visits/provenance.js';
import type { directory } from '@cwos/api-client';

/**
 * Pure presentation logic for the roster's two create flows: adding a worker
 * (a directory roster entry, distinct from inviting a login — see
 * `directory/service.ts`) and adding a client (initials-by-default; the full
 * name is write-only here and never echoed back). Kept DOM-free so validation
 * and payload-shaping are unit-tested without a browser.
 */

export interface WorkerDraft {
  displayName: string;
  roleTitle: string;
}

export const EMPTY_WORKER_DRAFT: WorkerDraft = { displayName: '', roleTitle: '' };

export function canSubmitWorker(draft: WorkerDraft): boolean {
  return draft.displayName.trim().length > 0;
}

export function workerCreatePayload(draft: WorkerDraft): directory.CreateWorkerInput {
  const roleTitle = draft.roleTitle.trim();
  return {
    displayName: draft.displayName.trim(),
    ...(roleTitle ? { roleTitle } : {}),
  };
}

export interface ClientDraft {
  firstName: string;
  lastName: string;
}

export const EMPTY_CLIENT_DRAFT: ClientDraft = { firstName: '', lastName: '' };

export function canSubmitClient(draft: ClientDraft): boolean {
  return draft.firstName.trim().length > 0 && draft.lastName.trim().length > 0;
}

export function clientCreatePayload(draft: ClientDraft): directory.CreateClientInput {
  return {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
  };
}

/**
 * EDITING A CLIENT RECORD.
 *
 * `PATCH /directory/clients/:id` has existed since the directory shipped and had
 * zero callers: once a client was created, nothing in the product could change a
 * misspelled name, a wrong date of birth, an EHR reference that moved, or a
 * status that should have gone to discharged. The only correction path was the
 * database.
 *
 * TWO RULES GOVERN EVERYTHING BELOW, and both come from the server.
 *
 * 1. OMITTED AND NULL ARE DIFFERENT REQUESTS. `updateClientSchema` is a
 *    `.partial()` (apps/api/src/domains/directory/schemas.ts:63) and the
 *    repository writes each nullable column as
 *    `case when $n then $n+1 else <column> end`, where `$n` is
 *    `Object.prototype.hasOwnProperty.call(patch, key)` (repository.ts:403,417).
 *    So a key that is absent leaves the column ALONE and a key that is `null`
 *    CLEARS it. A payload built as `{ dob: draft.dob || null }` therefore erases
 *    the date of birth of every client whose editor never opened that field —
 *    which is why {@link clientUpdatePayload} takes the ORIGINAL record and
 *    sends only what actually changed.
 *
 * 2. THE NAME IS WRITE-ONLY. No read path returns `first_name`/`last_name`
 *    except the audited `POST /directory/clients/:id/reveal-name`
 *    (service.ts:449). So the editor cannot PREFILL the name, and a blank name
 *    field means "leave the stored name alone" rather than "the client has no
 *    name". Blank is omitted; only a typed name is sent.
 */
export interface ClientEditDraft {
  /** Write-only. Blank means "leave the stored name alone" — never "clear it". */
  firstName: string;
  lastName: string;
  externalRef: string;
  /** `YYYY-MM-DD`, or blank for "not recorded". */
  dob: string;
  status: 'active' | 'inactive' | 'discharged';
  enrolledOn: string;
  dischargedOn: string;
  isTest: boolean;
}

/**
 * A draft seeded from the record as it stands.
 *
 * Takes the SINGLE-client read (`ClientDetail`), not a roster row: `dob` is not
 * on the roster at all (`ClientListView = Omit<ClientView, 'dob'>`,
 * service.ts:103), so seeding from a list row would show an empty date-of-birth
 * field for a client who has one and then, on save, offer to clear it.
 */
export function clientEditDraftFrom(client: directory.ClientDetail): ClientEditDraft {
  return {
    firstName: '',
    lastName: '',
    externalRef: client.externalRef ?? '',
    dob: client.dob ?? '',
    status: normalizeClientStatus(client.status),
    enrolledOn: client.enrolledOn ?? '',
    dischargedOn: client.dischargedOn ?? '',
    isTest: client.isTest,
  };
}

/**
 * The three statuses `clientStatusSchema` accepts (schemas.ts:16).
 *
 * A stored value outside the enum cannot be round-tripped honestly — sending it
 * back would be a 400, and silently rewriting it to `active` would change a
 * record the editor never touched. It falls back to `active` for the SELECT, and
 * {@link clientUpdatePayload} only emits `status` when the editor moved it, so
 * an unrecognised value is left exactly as it was found.
 */
export function normalizeClientStatus(raw: string): ClientEditDraft['status'] {
  return raw === 'inactive' || raw === 'discharged' || raw === 'active' ? raw : 'active';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Why this edit cannot be saved yet, or null.
 *
 * Mirrors the boundary rules — `firstName`/`lastName` are `.min(1)` when
 * present, and the dates are `/^\d{4}-\d{2}-\d{2}$/` (schemas.ts:10,63-73). A
 * `<input type="date">` cannot normally produce a malformed value, but it can be
 * typed into on some platforms, and a 400 rendered as "we couldn't save that" is
 * a worse answer than naming the field.
 */
export function clientEditProblem(draft: ClientEditDraft): string | null {
  const first = draft.firstName.trim();
  const last = draft.lastName.trim();
  if ((first && !last) || (last && !first)) {
    return 'Changing the name needs both parts — the initials are derived from the pair.';
  }
  for (const [label, value] of [
    ['date of birth', draft.dob],
    ['enrolment date', draft.enrolledOn],
    ['discharge date', draft.dischargedOn],
  ] as const) {
    if (value.trim() && !ISO_DATE.test(value.trim())) {
      return `The ${label} is not a date the record will accept (YYYY-MM-DD).`;
    }
  }
  return null;
}

/**
 * The PATCH body: only what changed, with `null` meaning "clear this".
 *
 * `programId` IS DELIBERATELY NOT EDITABLE HERE, and its absence is a decision
 * rather than an oversight. `updateClient` accepts it, but choosing one means
 * listing programs, and `GET /programs` is
 * `guard(..., 'program:read', { allowScoped: true })` (programs/routes.ts:441) —
 * a program-scoped reader is silently NARROWED rather than refused. A picker
 * built on that read would present one reader's subset as the organization's
 * whole set, which is the same defect this lane exists to avoid. Moving a client
 * between programs is an enrolment decision with its own scope question; it is
 * not a typo fix, and it does not belong on a form whose other fields are.
 */
export function clientUpdatePayload(
  draft: ClientEditDraft,
  original: directory.ClientDetail,
): directory.UpdateClientInput {
  const patch: directory.UpdateClientInput = {};

  // Write-only, so there is nothing to compare against: a typed name is a change
  // by definition, and a blank one is silence.
  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  if (firstName && lastName) {
    patch.firstName = firstName;
    patch.lastName = lastName;
  }

  assignNullable(patch, 'externalRef', draft.externalRef, original.externalRef);
  assignNullable(patch, 'dob', draft.dob, original.dob);
  assignNullable(patch, 'enrolledOn', draft.enrolledOn, original.enrolledOn);
  assignNullable(patch, 'dischargedOn', draft.dischargedOn, original.dischargedOn);

  if (draft.status !== normalizeClientStatus(original.status)) patch.status = draft.status;
  if (draft.isTest !== original.isTest) patch.isTest = draft.isTest;

  return patch;
}

/**
 * One nullable field: omit when unchanged, `null` when emptied, the value when
 * set. Written once rather than four times, because getting it wrong in one of
 * four places is how a field gets quietly cleared.
 */
function assignNullable<K extends 'externalRef' | 'dob' | 'enrolledOn' | 'dischargedOn'>(
  patch: directory.UpdateClientInput,
  key: K,
  drafted: string,
  original: string | null,
): void {
  const next = drafted.trim() === '' ? null : drafted.trim();
  if (next === (original ?? null)) return;
  patch[key] = next;
}

/** Nothing was changed, so there is nothing to send. */
export function clientEditIsEmpty(patch: directory.UpdateClientInput): boolean {
  return Object.keys(patch).length === 0;
}

/**
 * EDITING A WORKER RECORD.
 *
 * `grep -rn "updateWorker" apps/admin/` returned nothing. `PATCH
 * /directory/workers/:id` has existed since the directory shipped, the
 * repository writes every column of it (repository.ts:244), and
 * `CreateWorkerInput` accepts nine fields — while {@link workerCreatePayload}
 * above sends TWO. So an organization could add a name to a roster and then
 * never touch it again from inside the product.
 *
 * WHAT THAT COST, and it is not a list of missing form fields. `worker.
 * supervisor_id` is the canonical supervision edge in this API:
 * `reachesWorkerOnSupervisorEdge` resolves it and SIX domains consult it —
 * supervision, credentials, documentation, timekeeping, location and timesheets
 * (directory/routes.ts:885). A fresh organization has that column null on every
 * row and no way to set it, so a supervisor reaches nobody, supervision
 * compliance is a screen about nobody, and time corrections have no approver.
 * The same absence meant an invited login could never be joined to its worker
 * record (`worker.user_id`), a departed employee could never be marked as such,
 * and expected weekly hours — the denominator of every "below expected hours"
 * figure in the product — could never be set.
 *
 * THE THREE RULES BELOW ARE THE SERVER'S.
 *
 * 1. OMITTED AND NULL ARE DIFFERENT REQUESTS, exactly as for clients.
 *    `updateWorkerSchema` is a `.partial()` (schemas.ts:33-47) and
 *    `updateWorker` writes each nullable column as
 *    `case when $n then $n+1 else <column> end`, keyed on
 *    `hasOwnProperty` (repository.ts:244-262). A payload built as
 *    `{ hiredOn: draft.hiredOn || null }` erases the hire date of every worker
 *    whose editor never opened that field. {@link workerUpdatePayload}
 *    therefore takes the ORIGINAL record and sends only what actually moved.
 *
 * 2. `roleTitle` IS `.min(1).nullable()`, not `.min(0)`. An emptied box is
 *    `null` — "no title recorded" — and never `''`, which is a 400.
 *
 * 3. `supervisorId` AND `userId` ARE NOT ORDINARY FIELDS. `assertMayAssignAuthority`
 *    (service.ts:238) refuses both unless the caller holds `directory:assign`
 *    ORGANIZATION-WIDE, keyed on PRESENCE rather than value — clearing a
 *    supervisor is the same act as setting one. `directory:manage`, which the
 *    seeded `supervisor` role holds, is deliberately not enough: a caller who
 *    can write `supervisor_id` can appoint themselves anybody's supervisor, and
 *    a caller who can write `user_id` can BECOME another worker. So the two
 *    fields are gated separately in the UI, and this file never emits either
 *    one from a field the editor did not touch.
 */

/**
 * THE SUPERVISOR THE SERVER SENDS AND THIS CLIENT'S TYPE DOES NOT NAME.
 *
 * `directory.WorkerView` on the API carries `supervisorId` (service.ts:74,137)
 * and `PATCH /directory/workers/:id` replies with the whole of it — the route
 * says so where it explains why the write is gated like a read
 * (routes.ts:203). `@cwos/api-client`'s mirrored `Worker` interface does NOT
 * declare the field. That is a gap in the client package, it is owned by
 * another lane, and this app cannot close it.
 *
 * What it CAN do is refuse to guess. Three answers, kept apart:
 *
 *   a uuid   — this worker reports to that worker.
 *   `null`   — the server said there is no supervisor. A real answer.
 *   `undefined` — the field was not in the payload at all. NOT "no supervisor":
 *                 an unread value rendered as "None" is the confident zero this
 *                 codebase treats as a defect, and here it would invite an
 *                 administrator to "fix" an org chart that is already correct.
 *
 * The runtime read is narrow on purpose — one field, three outcomes, and no
 * assertion that the rest of the payload is anything other than what the type
 * says. Delete this the day the client type grows the field.
 */
export function readSupervisorId(worker: directory.Worker): string | null | undefined {
  const raw = (worker as { supervisorId?: unknown }).supervisorId;
  if (raw === null) return null;
  return typeof raw === 'string' && raw !== '' ? raw : undefined;
}

/**
 * The draft's value for a supervisor whose stored value was never read.
 *
 * A `<select>` has to hold SOMETHING, and every other value it could hold is a
 * claim: `''` says "nobody supervises this person" and a uuid says "this person
 * does". This is the fourth state — the one the honesty contract exists for —
 * and {@link workerUpdatePayload} emits nothing at all while it is selected.
 */
export const SUPERVISOR_UNREAD = '__supervisor_unread__';

export interface WorkerEditDraft {
  displayName: string;
  roleTitle: string;
  employmentStatus: 'active' | 'inactive' | 'terminated';
  /**
   * Kept as TEXT rather than `number | null`, because `Number('')` is `0` and a
   * contracted week of zero hours is a different statement from no contracted
   * week at all — and the second is what an empty box means.
   */
  expectedWeeklyHours: string;
  /** `YYYY-MM-DD`, or blank for "not recorded". */
  hiredOn: string;
  endedOn: string;
  /** A program id, or blank for "no primary program". */
  primaryProgramId: string;
  /** A worker id, blank for "no supervisor", or {@link SUPERVISOR_UNREAD}. */
  supervisorId: string;
  /** A user id, or blank for "no login joined to this record". */
  userId: string;
}

export function workerEditDraftFrom(worker: directory.Worker): WorkerEditDraft {
  const supervisor = readSupervisorId(worker);
  return {
    displayName: worker.displayName,
    roleTitle: worker.roleTitle ?? '',
    employmentStatus: normalizeEmploymentStatus(worker.employmentStatus),
    expectedWeeklyHours:
      worker.expectedWeeklyHours === null ? '' : String(worker.expectedWeeklyHours),
    hiredOn: worker.hiredOn ?? '',
    endedOn: worker.endedOn ?? '',
    primaryProgramId: worker.primaryProgramId ?? '',
    supervisorId: supervisor === undefined ? SUPERVISOR_UNREAD : (supervisor ?? ''),
    userId: worker.userId ?? '',
  };
}

/**
 * The three statuses `employmentStatusSchema` accepts (schemas.ts:14).
 *
 * Same treatment as {@link normalizeClientStatus}: a stored value outside the
 * enum falls back to `active` for the SELECT, and {@link workerUpdatePayload}
 * only emits `employmentStatus` when the editor MOVED it — so an unrecognised
 * value is left exactly as it was found rather than being quietly rewritten by
 * somebody who came to correct a spelling.
 */
export function normalizeEmploymentStatus(raw: string): WorkerEditDraft['employmentStatus'] {
  return raw === 'inactive' || raw === 'terminated' || raw === 'active' ? raw : 'active';
}

/**
 * Why this edit cannot be saved yet, or null.
 *
 * Mirrors the boundary rules so a 400 is never rendered as "we couldn't save
 * that": `displayName` is `.min(1)`, `expectedWeeklyHours` is
 * `.nonnegative().max(168)`, and both dates are `/^\d{4}-\d{2}-\d{2}$/`
 * (schemas.ts:33-47).
 */
export function workerEditProblem(draft: WorkerEditDraft): string | null {
  if (draft.displayName.trim() === '') {
    return 'A worker record needs a name. Clearing it is not something the record will accept.';
  }
  const hours = draft.expectedWeeklyHours.trim();
  if (hours !== '') {
    const n = Number(hours);
    if (!Number.isFinite(n) || n < 0 || n > 168) {
      return 'Expected weekly hours has to be a number between 0 and 168, or empty for “not set”.';
    }
  }
  for (const [label, value] of [
    ['start date', draft.hiredOn],
    ['end date', draft.endedOn],
  ] as const) {
    if (value.trim() && !ISO_DATE.test(value.trim())) {
      return `The ${label} is not a date the record will accept (YYYY-MM-DD).`;
    }
  }
  return null;
}

/**
 * The PATCH body: only what changed, with `null` meaning "clear this".
 *
 * `timeSource` IS DELIBERATELY NOT HERE, and its absence is a decision. It
 * chooses whether a worker's hours come from their own clock-ins or from an
 * import, which is a statement about where this organization's time DATA comes
 * from rather than a correction to one person's record — and flipping it on a
 * form whose other fields are typo fixes would silently orphan every session
 * already recorded against the other source. It belongs with the organization's
 * time settings, which is where the decision is actually made.
 */
export function workerUpdatePayload(
  draft: WorkerEditDraft,
  original: directory.Worker,
): directory.UpdateWorkerInput {
  const patch: directory.UpdateWorkerInput = {};

  const displayName = draft.displayName.trim();
  if (displayName !== '' && displayName !== original.displayName) patch.displayName = displayName;

  assignNullableField(patch, 'roleTitle', draft.roleTitle, original.roleTitle);
  assignNullableField(patch, 'hiredOn', draft.hiredOn, original.hiredOn);
  assignNullableField(patch, 'endedOn', draft.endedOn, original.endedOn);
  assignNullableField(patch, 'primaryProgramId', draft.primaryProgramId, original.primaryProgramId);
  assignNullableField(patch, 'userId', draft.userId, original.userId);

  if (draft.employmentStatus !== normalizeEmploymentStatus(original.employmentStatus)) {
    patch.employmentStatus = draft.employmentStatus;
  }

  const hours = draft.expectedWeeklyHours.trim();
  const nextHours = hours === '' ? null : Number(hours);
  if (nextHours !== (original.expectedWeeklyHours ?? null)) patch.expectedWeeklyHours = nextHours;

  // The fourth state, and the only field that has one: while the stored value
  // is unread there is nothing to compare against and nothing to send. An
  // editor who picks somebody has made a statement, and that statement goes —
  // even against an unknown original, where it is a change by definition.
  if (draft.supervisorId !== SUPERVISOR_UNREAD) {
    const next = draft.supervisorId.trim() === '' ? null : draft.supervisorId.trim();
    const before = readSupervisorId(original);
    if (before === undefined || next !== before) patch.supervisorId = next;
  }

  return patch;
}

/** Nothing was changed, so there is nothing to send. */
export function workerEditIsEmpty(patch: directory.UpdateWorkerInput): boolean {
  return Object.keys(patch).length === 0;
}

/**
 * Whether a patch touches either of the two AUTHORITY fields.
 *
 * `assertMayAssignAuthority` keys on PRESENCE, not value (service.ts:230), so
 * this asks the same question the server will: is the key in the object? The
 * screen uses it to decide whether a save is about to need `directory:assign`,
 * so a refusal can be explained in terms of the field that caused it rather
 * than as "we couldn't save that".
 */
export function workerPatchAssignsAuthority(patch: directory.UpdateWorkerInput): boolean {
  return 'supervisorId' in patch || 'userId' in patch;
}

/**
 * One nullable field: omit when unchanged, `null` when emptied, the value when
 * set. The worker half of the rule {@link assignNullable} states for clients —
 * written once here rather than five times, for the same reason.
 *
 * The cast is to the index signature the mapped `UpdateWorkerInput` does not
 * publish; the KEY is constrained to the four nullable string members, so
 * nothing else can reach it.
 */
function assignNullableField(
  patch: directory.UpdateWorkerInput,
  key: 'roleTitle' | 'hiredOn' | 'endedOn' | 'primaryProgramId' | 'userId',
  drafted: string,
  original: string | null,
): void {
  const next = drafted.trim() === '' ? null : drafted.trim();
  if (next === (original ?? null)) return;
  patch[key] = next;
}

/**
 * PH-UX-002 — where a roster row came from, from the three facts the API
 * measures on every client (`source`, `integrationId`, `lastSyncedAt`), and
 * never from `externalRef`, which a person can type.
 *
 * `source` is the ORIGIN (who created the row) and does not move; the
 * installation stamp is an OBSERVATION (the vendor listed this person, and
 * when) and is set on hand-entered rows too — so "entered here, and Ritten
 * lists them" is a sentence this can say. An installation the reader cannot
 * resolve (no `integrations:read`, or an unknown id) is "the EHR", never a
 * guessed name. A server that predates the columns sends none of the three;
 * that is "not recorded", not "entered here".
 */
export function clientOriginSentence(
  row: {
    source?: string | undefined;
    integrationId?: string | null | undefined;
    lastSyncedAt?: string | null | undefined;
  },
  connectors: ConnectorLookup,
  formatInstant: (iso: string) => string,
): string {
  if (row.source === undefined) return 'Origin not recorded';
  const name =
    row.integrationId != null
      ? (connectors.get(row.integrationId)?.displayName ?? 'the EHR')
      : null;
  const when = row.lastSyncedAt != null ? formatInstant(row.lastSyncedAt) : null;
  if (row.source === 'imported') {
    const from = `Imported from ${name ?? 'the EHR'}`;
    return when ? `${from} · last listed ${when}` : from;
  }
  if (name !== null) {
    return when
      ? `Entered here · also listed in ${name} (${when})`
      : `Entered here · also listed in ${name}`;
  }
  return 'Entered here';
}
